import { Injectable, NgZone } from '@angular/core';
import { init, loadRemote } from '@module-federation/enhanced/runtime';
import { iteroAppId } from 'app/debug/event-bus-tester/event-bus-tester.component';
import { RenderOptions } from './types/render-options';
import { IteroRemoteApplication } from './types/itero-remote-application';
import { EupRoutesService } from '@core/eupRoutes.service';

const ATTEMPTS = 3;

@Injectable({
	providedIn: 'root',
})
export class RemoteApplicationLoaderService {
	private remoteModulePromiseResolve: (value: IteroRemoteApplication) => void;
	private remoteModulePromiseReject: (reason: Error) => void;
	private initialized = false;

	private remoteModulePromise = new Promise<IteroRemoteApplication>((resolve, reject) => {
		this.remoteModulePromiseResolve = resolve;
		this.remoteModulePromiseReject = reject;
	});

	constructor(
		private readonly routingService: EupRoutesService,
		private readonly ngZone: NgZone
	) {}

	async initialize() {
		if (this.initialized) {
			return;
		}

		const remoteApplicationLoaderUrl = this.routingService.remoteApplicationLoader;

		if (!remoteApplicationLoaderUrl) {
			throw new Error('[RemoteApplicationService] Missing remoteApplicationLoaderUrl in appSettings.json');
		}

		init({
			name: iteroAppId,
			remotes: [
				{
					name: 'remote-application-loader',
					entry: remoteApplicationLoaderUrl,
					type: 'module',
				},
			],
		});

		let attemptsLeft = ATTEMPTS;

		while (attemptsLeft > 0) {
			try {
				attemptsLeft--;

				const remoteModule = await loadRemote<IteroRemoteApplication>('remote-application-loader').then(async (rm) => {
					await rm.initRemotes({
						ngZone: this.ngZone,
						// You can override remote URLs below
						overrideAppsConfig: [
							// {
							// 	appId: 'image-hub',
							// 	url: 'http://localhost:4211/remoteEntry.js',
							// },
						],
					});
					return rm;
				});
				this.remoteModulePromiseResolve(remoteModule);
				break;
			} catch (e) {
				if (attemptsLeft < 0) {
					this.remoteModulePromiseReject(e);
					break;
				}

				// Wait before retry
				await new Promise((resolve) => setTimeout(resolve, 1000));
			}
		}

		this.initialized = true;
	}

	async renderRemote(renderOptions: RenderOptions) {
		await this.initialize();

		await (await this.remoteModulePromise).renderRemote(renderOptions);
	}
}

